import { LoadedFont } from "./loader";
import { TextAlignment, TextSpec, generateTextMeshBuffer } from "./sdfText";
import { TextShader } from "./rendering/TextShader";
import { Transformation } from "./Transformation";
import { TextParams } from "./sequencer";

export class Text {
  public readonly transformation: Transformation = new Transformation(
    [1, 1, 1],
    [0, 0, 0],
  );

  constructor(
    private readonly gl: WebGL2RenderingContext,
    private readonly shader: TextShader,
    private readonly font: LoadedFont,
    private readonly vertexBuffer: WebGLBuffer,
    private readonly vertexCount: number,
    private readonly indexBuffer: WebGLBuffer,
    private readonly vao: WebGLVertexArrayObject,
  ) {}

  public static create(
    shader: TextShader,
    font: LoadedFont,
    spec: TextSpec,
  ): Text {
    const gl = shader.gl;
    const { vertexBuffer, vertexCount, indexBuffer } = generateTextMeshBuffer({
      gl,
      font: font.description,
      spec,
    });

    const vao = gl.createVertexArray()!;
    gl.bindVertexArray(vao);

    gl.bindBuffer(gl.ARRAY_BUFFER, vertexBuffer);
    gl.enableVertexAttribArray(0);
    gl.vertexAttribPointer(0, 4, gl.FLOAT, false, 0, 0);

    gl.bindBuffer(gl.ARRAY_BUFFER, indexBuffer);
    gl.enableVertexAttribArray(1);
    gl.vertexAttribIPointer(1, 1, gl.UNSIGNED_SHORT, 0, 0);

    return new Text(
      gl,
      shader,
      font,
      vertexBuffer,
      vertexCount,
      indexBuffer,
      vao,
    );
  }

  public render({
    now,
    resolution,
    opacity,
    params,
  }: {
    now: number;
    resolution: [number, number];
    opacity: number;
    params?: TextParams;
  }) {
    this.gl.bindVertexArray(this.vao);
    this.shader.use({
      now,
      resolution,
      transform: this.transformation.matrix(),
      opacity,
      font: this.font,
      waviness: params?.waviness,
      distortion: params?.distortion,
      color: params?.color || [1, 1, 1],
    });
    this.gl.bindTexture(this.gl.TEXTURE_2D, this.font.texture);
    this.gl.drawArrays(this.gl.TRIANGLES, 0, this.vertexCount);
  }

  public destroy() {
    this.gl.deleteBuffer(this.vertexBuffer);
    this.gl.deleteBuffer(this.indexBuffer);
    this.gl.deleteVertexArray(this.vao);
  }
}
